<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import type { AcceptBidResponseDto } from "@/model/championship/accept-bid-response-dto"
import type { ChampionshipRegistrationAcceptBidTask } from "@/model/championship/championship-registration-accept-bid-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { useHttpClient, useHttpGet, useLoadingState } from "@/vf"
import { toRefs } from "vue"

const props = defineProps<TasksProps<ChampionshipRegistrationAcceptBidTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const http = useHttpClient()

const { data: bidAcceptance } = await useHttpGet<AcceptBidResponseDto>(
    `/championship/registration/${task.value.registrationId}/accept-bid/${task.value.id}`,
)

const submitting = useLoadingState()

function submit() {
    submitting.while(async () => {
        await http.post(
            `/championship/registration/${task.value.registrationId}/accept-bid/${task.value.id}?viewMode=${props.viewMode}`,
            {
                teams: bidAcceptance.value?.teams,
            },
        )
        emit("updated")
    })
}

const trans = defineTranslations({
    title: {
        de: "Bid-Annahme",
        en: "Bid Acceptance",
    },
    description: {
        de: "Bitte gib für jedes Team und Bid an, ob du es annehmen möchtest, solltest du es gewinnen.",
        en: "Please indicate for each team and bid whether you want to accept it if you win.",
    },
    save: {
        de: "Speichern",
        en: "Save",
    },
})
</script>
<template>
    <div class="p-4" v-if="bidAcceptance">
        <p class="mb-4">{{ trans("description") }}</p>

        <div v-for="team in bidAcceptance.teams" :key="team.id" class="mb-5">
            <h5 class="mb-3">
                {{ team.teamName }}
                <small class="text-muted">{{ team.categoryName }}</small>
            </h5>

            <div v-for="bid in team.bids" :key="bid.id" class="mb-3 pl-3">
                <div class="font-weight-bold mb-2">{{ bid.name }}</div>
                <div class="gap-4">
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`accept-${team.id}-${bid.id}`"
                            :name="`bid-${team.id}-${bid.id}`"
                            :value="true"
                            v-model="bid.accepted"
                            :disabled="task.state !== 'open'"
                        />
                        <label class="form-check-label" :for="`accept-${team.id}-${bid.id}`">
                            {{ $t("@tasks:tasks.championship_registration.wizard.bid.accept", { bid: bid.name }) }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`decline-${team.id}-${bid.id}`"
                            :name="`bid-${team.id}-${bid.id}`"
                            :value="false"
                            v-model="bid.accepted"
                            :disabled="task.state !== 'open'"
                        />
                        <label class="form-check-label" :for="`decline-${team.id}-${bid.id}`">
                            {{ $t("@tasks:tasks.championship_registration.wizard.bid.decline", { bid: bid.name }) }}
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end mt-3" v-if="task.state === 'open'">
            <LoadingButton :loading="submitting" @click="submit()">
                {{ trans("save") }}
            </LoadingButton>
        </div>
    </div>
</template>
